import { FunctionComponent, ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { KatProgress } from '@amzn/katal-react';
import { useBundle } from '@amzn/react-arb-tools';
import {
  AsxMarkdownContent,
  BackButton,
  Heading,
  HEADING_TAG,
  HighlightedCard,
  Stepper,
  useDeviceSize,
} from '@amzn/sss-website-theme';

import { FormStrings, INTAKE_FORM_PAGE_BUNDLE_NAME } from 'src/constants';
import { ROUTES } from 'src/enums/Routes';
import { t } from 'src/helpers';

import styles from './IntakeFormTemplate.module.scss';

export interface IntakeFormTemplateProps {
  children: ReactNode;
  activeStep: number;
}

export const IntakeFormTemplate: FunctionComponent<IntakeFormTemplateProps> = ({ children, activeStep }) => {
  const [formBundle, isFormBundleLoading] = useBundle(INTAKE_FORM_PAGE_BUNDLE_NAME);
  const { isSmallDesktop } = useDeviceSize();
  const location = useLocation();

  // Get previous URL, check if it's from account pages (case-insensitive)
  const previousUrl = location.state?.previousUrl || document.referrer;
  const isFromAccount = previousUrl?.toLowerCase().includes('/account');

  const stepsContent = [
    {
      title: '1',
      label: t(formBundle, FormStrings.PERSONAL_INFORMATION_HEADER),
      href: ROUTES.INTAKE_PAGE_STEP_1,
    },
    { title: '2', label: t(formBundle, FormStrings.COMMITMENTS_INFORMATION_HEADER) },
  ];

  useEffect(() => {
    if (isSmallDesktop) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [activeStep, isSmallDesktop]);

  if (isFormBundleLoading) {
    // istanbul ignore next
    return (
      <div role="status">
        <KatProgress katAriaLabel={t(formBundle, 'intakeForm_loading')} indeterminate size="medium" type="circular" />
      </div>
    );
  }

  return (
    <div className={styles.intakeFormTemplate}>
      <div className={styles.topBar}>
        <div className={styles.backButtonContainer}>
          <BackButton
            variant="default"
            className={styles.backButton}
            label={t(
              formBundle,
              isFromAccount ? FormStrings.INTAKE_FORM_BACK_BUTTON_MY_ACCOUNT : FormStrings.INTAKE_FORM_BACK_BUTTON
            )}
            data-testid="back-button"
            url={isFromAccount ? previousUrl : ROUTES.CREDITS_LANDING}
            forceServerSideNav
          />
        </div>
        {isSmallDesktop && <Stepper activeStep={activeStep} steps={stepsContent} />}
      </div>
      <div className={styles.content}>
        <div className={styles.text}>
          <Heading as={HEADING_TAG.H2} className={styles.intakeFormHeading}>
            {t(formBundle, FormStrings.INTAKE_FORM_TITLE)}
          </Heading>
          <div className={styles.highlightedCardWrapper}>
            <AsxMarkdownContent
              isRebrandingActive
              className={styles.formCopy}
              copy={t(formBundle, FormStrings.INTAKE_FORM_COPY_TWO)}
            />
            <HighlightedCard
              title={t(formBundle, FormStrings.INTAKE_FORM_HIGHLIGHTED_CARD_TITLE)}
              subtitle={t(formBundle, FormStrings.INTAKE_FORM_HIGHLIGHTED_CARD_SUBTITLE)}
              items={[
                t(formBundle, FormStrings.INTAKE_FORM_HIGHLIGHTED_CARD_ITEM_1),
                t(formBundle, FormStrings.INTAKE_FORM_HIGHLIGHTED_CARD_ITEM_2),
              ]}
            />
          </div>
          <p className={styles.formCopy}>{t(formBundle, FormStrings.INTAKE_FORM_COPY_FOUR)}</p>
        </div>
        {!isSmallDesktop && (
          <div className={styles.stepper}>
            <Stepper activeStep={activeStep} steps={stepsContent} />
          </div>
        )}

        <div className={styles.intakeFormWrapper}>{children}</div>
      </div>
    </div>
  );
};
